// components/onboardingflow/ActionScreen.jsx
//
// Step 8 (final) — Books appointment / starts assessment / enrolls program
// Renders the appropriate confirmation + CTA for the chosen action.
//
// Props:
//   action:      "book" | "assess" | "enroll"
//   riskTier:    "Low" | "Moderate" | "High"
//   patientData: object
//   onBook()    — navigate to appointment booking
//   onAssess()  — navigate to health assessment
//   onEnroll()  — navigate to wellness program enrollment

import React, { useEffect, useState } from "react";
import styles from "./Actionscreen.module.css";

const ACTION_CONFIG = {
  book: {
    emoji:     "🗓️",
    title:     "Book Your Appointment",
    subtitle:  "Choose a convenient time to speak with a specialist.",
    cta:       "Find Available Slots",
    ctaAction: "onBook",
    steps: [
      { icon: "👤", text: "Choose your specialist" },
      { icon: "📅", text: "Pick a date & time" },
      { icon: "✅", text: "Get instant confirmation" },
    ],
    note: "Most slots available within 48 hours.",
  },
  assess: {
    emoji:     "📋",
    title:     "Start Your Assessment",
    subtitle:  "A detailed health evaluation to know your exact health status.",
    cta:       "Begin Assessment",
    ctaAction: "onAssess",
    steps: [
      { icon: "🩺", text: "Complete full health questionnaire" },
      { icon: "📊", text: "Get your personalised health report" },
      { icon: "💡", text: "Receive actionable recommendations" },
    ],
    note: "Takes about 10–15 minutes to complete.",
  },
  enroll: {
    emoji:     "🏥",
    title:     "Join Our Wellness Program",
    subtitle:  "Guided, ongoing support to help you live healthier every day.",
    cta:       "Enroll Now",
    ctaAction: "onEnroll",
    steps: [
      { icon: "🎯", text: "Set your personal health goals" },
      { icon: "📱", text: "Track progress with daily check-ins" },
      { icon: "🤝", text: "Get support from your care team" },
    ],
    note: "Your first week is completely free.",
  },
};

const ActionScreen = ({
  action = "book",
  riskTier = "Low",
  patientData = {},
  onBook,
  onAssess,
  onEnroll,
}) => {
  const config  = ACTION_CONFIG[action] || ACTION_CONFIG.book;
  const name    = patientData?.name ? patientData.name.split(" ")[0] : "";
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setReady(true), 200);
    return () => clearTimeout(t);
  }, []);

  const handleCta = () => {
    if (config.ctaAction === "onBook")    onBook?.();
    if (config.ctaAction === "onAssess")  onAssess?.();
    if (config.ctaAction === "onEnroll")  onEnroll?.();
  };

  return (
    <div className={`${styles.wrapper} ${ready ? styles.visible : ""}`}>
      {/* Top illustration area */}
      <div className={styles.illustration}>
        <div className={styles.circle}>
          <span className={styles.circleEmoji}>{config.emoji}</span>
        </div>
        {name && (
          <p className={`ava-text-caption ${styles.greeting}`}>
            You're all set{name ? `, ${name}` : ""}!
          </p>
        )}
      </div>

      <h2 className={`ava-heading-md ${styles.title}`}>{config.title}</h2>
      <p className={`ava-text-body ${styles.subtitle}`}>{config.subtitle}</p>

      {/* Steps */}
      <div className={styles.steps}>
        {config.steps.map((s, i) => (
          <div key={i} className={styles.step} style={{ animationDelay: `${i * 80}ms` }}>
            <div className={styles.stepNum}>{i + 1}</div>
            <span className={styles.stepIcon}>{s.icon}</span>
            <span className={`ava-text-body ${styles.stepText}`}>{s.text}</span>
          </div>
        ))}
      </div>

      {/* CTA */}
      <button
        className={`btn-primary ${styles.cta}`}
        onClick={handleCta}
        type="button"
      >
        {config.cta} →
      </button>

      {/* Note */}
      <p className={`ava-text-caption ${styles.note}`}>
        ℹ️ {config.note}
      </p>

      {/* Switch actions */}
      <div className={styles.altActions}>
        {action !== "book" && (
          <button type="button" className={styles.altBtn} onClick={onBook}>
            📅 Book an appointment instead
          </button>
        )}
        {action !== "assess" && (
          <button type="button" className={styles.altBtn} onClick={onAssess}>
            📋 Start assessment instead
          </button>
        )}
      </div>
    </div>
  );
};

export default ActionScreen;